import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { map, Observable, startWith } from 'rxjs';
import { PropertyFormService } from '../../services/property-form.service';
import { Organization } from '../../../../organizations/models/Organization';

@Component({
  selector: 'property-form-basic-information',
  templateUrl: './property-form-basic-information.component.html',
  styleUrls: ['./property-form-basic-information.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIcon,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatOption,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatAutocompleteModule
  ]
})
export class PropertyFormBasicInformationComponent implements OnInit, AfterViewInit {

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  @Input() propertyFormService!: PropertyFormService;
  @Input('organizations') organizations!: Organization[];
  @Output() selectedOrgIdEvent = new EventEmitter<number>();

  form!: FormGroup;
  panelOpen: boolean = false;

  orgControl = new FormControl('', Validators.required);
  filteredOrganizations!: Observable<any[]>;
  organizationSelect!: Organization | undefined;

  constructor(
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      organization: ['', Validators.required],
      property_reference_id: ['', Validators.pattern(/[\S]/)],
      property_name: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      property_type: ['', [Validators.required, Validators.min(1), Validators.max(5)]],
      accommodates: [0, Validators.pattern(/[\S]/)],
      bathrooms: [0, Validators.pattern(/[\S]/)],
      bedrooms: [0, Validators.pattern(/[\S]/)],
      description: ['', Validators.pattern(/[\S]/)],
    });

    this.propertyFormService.existingPropertyLoaded$.subscribe(state => {
      if (!state) {
        return;
      }

      let organization = this.organizations.find(org => org.organization_id === state.organization);
      this.form.patchValue({
        organization: organization,
        property_reference_id: state.propertyReferenceId,
        property_name: state.propertyName,
        property_type: state.propertyTypeId,
        accommodates: state.accommodates,
        bathrooms: state.bathrooms,
        bedrooms: state.bedrooms,
        description: state.propertyDescription
      });

      this.orgControl.setValue(this.form.value.organization);
      this.organizationSelect = this.form.value.organization;
      this.orgControl.disable();

      this.form.markAllAsTouched();
      this.form.markAsDirty();
      this.updateStateAndStepValidity(this.form.value);
    });

    this.form.valueChanges.subscribe(state => this.updateStateAndStepValidity(state));
  }

  ngAfterViewInit() {
    this.filteredOrganizations = this.orgControl.valueChanges.pipe(
      startWith(''),
      map(value => this._orgFilter(value || '')),
    );

    this.autocompleteTrigger.panelClosingActions.subscribe((e) => {      
      if (!(e && e.source)) {
        this.orgControl.setValue('');
        this.autocompleteTrigger.closePanel();
      }
    });
    this.cdr.detectChanges()
  }

  private _orgFilter(value: string): Organization[] {
    return this.organizations.filter(org => org.organization_name.toLowerCase().includes(value.toString().toLowerCase()));
  }

  private updateStateAndStepValidity(state: any) {
    this.propertyFormService.updateBasicInformationState(state);
    this.updateStepValidity();
  }

  private updateStepValidity() {
    this.propertyFormService.setBasicInformationStepValidity(this.form.valid);
  }

  onOrgSelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.organizationSelect = event.option.value;
    this.form.controls['organization'].setValue(event.option.value);
    this.selectedOrgIdEvent.emit(this.organizationSelect?.organization_id);
  }

  displayOrgFn(org: Organization): string {
    return org && org.organization_name ? org.organization_name : '';
  }
}